/** Pagination — page nav with first/prev/numbers/next/last. */
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "../../lib/cn";

function PaginationButton({ active, disabled, onClick, children, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      className={cn(
        "inline-flex items-center justify-center min-w-[36px] h-9 px-3 rounded-full text-[13px] font-medium transition-colors border",
        active
          ? "bg-bg-elev border-line-2 text-text shadow-card"
          : "border-transparent text-text-dim hover:text-text hover:bg-surface-2",
        disabled && "opacity-40 cursor-not-allowed hover:bg-transparent"
      )}
    >
      {children}
    </button>
  );
}

export function Pagination({ page, pageCount, onPageChange, className, siblingCount = 1 }) {
  if (pageCount <= 1) return null;
  const items = [];
  const left = Math.max(2, page - siblingCount);
  const right = Math.min(pageCount - 1, page + siblingCount);
  items.push(1);
  if (left > 2) items.push("…");
  for (let i = left; i <= right; i++) items.push(i);
  if (right < pageCount - 1) items.push("…");
  if (pageCount > 1) items.push(pageCount);

  return (
    <nav aria-label="Pagination" data-testid="pagination" className={cn("flex items-center gap-1", className)}>
      <PaginationButton ariaLabel="First page" disabled={page === 1} onClick={() => onPageChange(1)}>
        <ChevronsLeft size={14} />
      </PaginationButton>
      <PaginationButton ariaLabel="Previous page" disabled={page === 1} onClick={() => onPageChange(page - 1)}>
        <ChevronLeft size={14} />
      </PaginationButton>
      {items.map((it, i) =>
        it === "…" ? (
          <span key={`gap-${i}`} className="px-2 text-text-faint" aria-hidden="true">
            …
          </span>
        ) : (
          <PaginationButton
            key={it}
            ariaLabel={`Page ${it}`}
            active={it === page}
            onClick={() => onPageChange(it)}
          >
            {it}
          </PaginationButton>
        )
      )}
      <PaginationButton
        ariaLabel="Next page"
        disabled={page === pageCount}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight size={14} />
      </PaginationButton>
      <PaginationButton
        ariaLabel="Last page"
        disabled={page === pageCount}
        onClick={() => onPageChange(pageCount)}
      >
        <ChevronsRight size={14} />
      </PaginationButton>
    </nav>
  );
}
